import React from "react";
import { useTranslation } from "../../hooks/useTranslation";
import { useTheme } from "../../contexts/ThemeContext";

interface NotesModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (note: string) => void;
    initialNote?: string;
}

export default function NotesModal({ isOpen, onClose, onSave, initialNote = "" }: NotesModalProps) {
    const { t, isRTL } = useTranslation();
    const { primaryColor } = useTheme();
    const [note, setNote] = React.useState(initialNote);

    React.useEffect(() => {
        setNote(initialNote);
    }, [initialNote, isOpen]);

    if (!isOpen) return null;

    const handleSave = () => {
        onSave(note);
        onClose();
    };

    // Function to count characters
    const getCharCount = (text: string) => {
        return text.length;
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        if (getCharCount(value) <= 100) {
            setNote(value);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
            <div className="bg-white rounded-xl p-5 w-[90%] max-w-md shadow-xl relative">
                {/* Header */}
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-bold text-gray-800">{t('cart.notesTitle')}</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 text-xl hover:text-gray-700 transition-colors"
                    >
                        ×
                    </button>
                </div>

                {/* Textarea */}
                <textarea
                    className="w-full h-28 border border-gray-200 bg-white text-gray-900 rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-1 placeholder-gray-400"
                    placeholder={t('cart.notesPlaceholder')}
                    value={note}
                    onChange={handleChange}
                    dir={isRTL ? 'rtl' : 'ltr'}
                    style={{ 
                        backgroundColor: 'white', 
                        textAlign: isRTL ? 'right' : 'left',
                        borderColor: 'var(--border)'
                    }}
                    onFocus={(e) => {
                        e.target.style.borderColor = 'var(--primary)'
                        e.target.style.boxShadow = '0 0 0 1px var(--primary)'
                    }}
                    onBlur={(e) => {
                        e.target.style.borderColor = 'var(--border)'
                        e.target.style.boxShadow = 'none'
                    }}
                />

                {/* Character Counter */}
                <div className={`text-xs text-gray-500 mt-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {getCharCount(note)}/100 {t('cart.characterCount')}
                </div>

                {/* Save Button */}
                <button
                    onClick={handleSave}
                    className="text-white w-full mt-4 py-2 rounded-lg font-bold transition-colors"
                    style={{ 
                        backgroundColor: primaryColor || '#C44536',
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '0.9'
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '1'
                    }}
                >
                    {t('cart.saveNotes')}
                </button>
            </div>
        </div>
    );
}
